import React, { createContext, useCallback, useContext, useState } from 'react';

const COLORS = {
  maroon: '#7B1F2E',
  border: '#E5DBD2',
  textPrimary: '#2D2A2E',
  textSecondary: '#6B6168',
  bgLight: '#FAF7F2',
};

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        tone: opts.tone || 'default',
        details: opts.details || null,
        resolve,
      });
    });
  }, []);

  const close = (value) => {
    if (state) state.resolve(value);
    setState(null);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/45">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            style={{ border: `1px solid ${COLORS.border}` }}
            role="dialog"
            aria-modal="true"
          >
            <div className="px-6 pt-6 pb-3">
              <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                {state.title}
              </h3>
              {state.message && (
                <p className="text-sm leading-relaxed" style={{ color: COLORS.textSecondary }}>
                  {state.message}
                </p>
              )}
              {state.details && (
                <div
                  className="mt-3 rounded-lg p-3 text-xs"
                  style={{ background: COLORS.bgLight, color: COLORS.textSecondary }}
                >
                  {state.details}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4" style={{ background: COLORS.bgLight }}>
              <button
                type="button"
                onClick={() => close(false)}
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors hover:bg-gray-100"
                style={{ color: COLORS.textSecondary, background: 'white', border: `1px solid ${COLORS.border}` }}
              >
                {state.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                className="px-4 py-2 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-90"
                style={{
                  background: state.tone === 'danger' ? COLORS.maroon : COLORS.maroon,
                }}
                autoFocus
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
